import mixpanel, { Dict } from "mixpanel-browser";

import { env } from "@/config/env";

import { getMode } from "./env-mode";

/**
 * Mixpanel Analytics Utility
 *
 * Handles analytics tracking across different environments:
 * - Development: Only logs to console
 * - Staging: Tracks to "Sharp Performance - Dev" Mixpanel project
 * - Production: Tracks to "Sharp Performance" Mixpanel project
 *
 * All Lotus events include a `project: "lotus"` super property to distinguish
 * them from other Sharp products within the shared Mixpanel projects.
 *
 * Environment detection uses Vite's MODE (development, staging, production, test).
 */

const isDevelopment = () => {
  const mode = getMode();
  return mode === "development" || mode === "test";
};

const isStaging = () => getMode() === "staging";

/**
 * Initialize Mixpanel with appropriate token based on environment
 */
export function initMixpanel() {
  if (isDevelopment()) {
    // Development: Don't initialize Mixpanel, just log
    console.log(
      "[Mixpanel] Running in development mode - events will be logged to console",
    );
    return;
  }

  // Use the token from the environment-specific .env file
  const token = env.VITE_MIXPANEL_TOKEN;

  if (!token) {
    console.error(
      "[Mixpanel] Missing token for environment:",
      getMode(),
      "- Ensure VITE_MIXPANEL_TOKEN is defined in your .env file",
    );
    return;
  }

  // Initialize Mixpanel
  mixpanel.init(token, {
    debug: isStaging(), // Enable debug in staging
    track_pageview: "full-url", // Automatic page tracking
    persistence: "localStorage",
    ignore_dnt: false, // Respect Do Not Track
  });

  // Set super properties (included in all events)
  mixpanel.register({
    project: "lotus",
  });

  console.log(`[Mixpanel] Initialized for ${getMode()} environment`);
}

/**
 * Track an event in Mixpanel
 *
 * @param eventName - Name of the event
 * @param properties - Additional properties to track
 *
 * @example
 * trackEvent("Button Clicked", { button_name: "submit", page: "login" })
 */
export function trackEvent(eventName: string, properties?: Dict) {
  if (isDevelopment()) {
    console.log("[Mixpanel] Track Event:", eventName, properties);
    return;
  }

  mixpanel.track(eventName, properties);
}

/**
 * Identify a user in Mixpanel
 *
 * @param userId - Unique identifier for the user
 *
 * @example
 * identifyUser("user-123")
 */
export function identifyUser(userId: string) {
  if (isDevelopment()) {
    console.log("[Mixpanel] Identify User:", userId);
    return;
  }

  mixpanel.identify(userId);
}

/**
 * Set user properties in Mixpanel
 *
 * @param properties - User properties to set
 *
 * @example
 * setUserProperties({ name: "John", email: "john@example.com" })
 */
export function setUserProperties(properties: Dict) {
  if (isDevelopment()) {
    console.log("[Mixpanel] Set User Properties:", properties);
    return;
  }

  mixpanel.people.set(properties);
}

/**
 * Register Mixpanel super properties (attached to every subsequent event).
 *
 * @param properties - Super properties to register
 *
 * @example
 * registerMixpanelSuperProperties({ customer_id: 5392, entry_point: "web" })
 */
export function registerMixpanelSuperProperties(properties: Dict) {
  if (isDevelopment()) {
    console.log("[Mixpanel] Register Super Properties:", properties);
    return;
  }

  mixpanel.register(properties);
}

/**
 * Clear a single Mixpanel super property so it stops being attached to
 * subsequent events. Useful when a session transitions from identified to
 * anonymous (e.g. a different member opens the embed in the same browser)
 * and a previously-registered value would otherwise leak across visits.
 *
 * @param property - The super property key to clear
 *
 * @example
 * unregisterMixpanelSuperProperty("customer_id")
 */
export function unregisterMixpanelSuperProperty(property: string) {
  if (isDevelopment()) {
    console.log("[Mixpanel] Unregister Super Property:", property);
    return;
  }

  mixpanel.unregister(property);
}

/**
 * Reset Mixpanel state (useful for logout)
 */
export function resetMixpanel() {
  if (isDevelopment()) {
    console.log("[Mixpanel] Reset");
    return;
  }

  mixpanel.reset();
}

/**
 * Track a page view manually
 * Note: Automatic page tracking is enabled by default
 *
 * @param pageName - Name of the page
 * @param properties - Additional properties
 */
export function trackPageView(pageName: string, properties?: Dict) {
  trackEvent("Page Viewed", {
    page_name: pageName,
    ...properties,
  });
}

/**
 * Enable automatic form tracking
 *
 * This sets up automatic tracking for form submissions across the application.
 * By default, it tracks all forms, but you can provide a custom selector.
 *
 * The tracked event includes:
 * - Form ID or name
 * - Form action URL
 * - Number of fields
 *
 * @param selector - CSS selector for forms to track (default: "form")
 * @param eventName - Custom event name (default: "Form Submitted")
 *
 * @example
 * // Track all forms
 * enableFormTracking();
 *
 * @example
 * // Track only login forms
 * enableFormTracking("form[data-form='login']", "Login Form Submitted");
 */
export function enableFormTracking(
  selector: string = "form",
  eventName: string = "Form Submitted",
) {
  if (isDevelopment()) {
    console.log(
      `[Mixpanel] Form tracking enabled in development mode for selector: ${selector}`,
    );

    // In development, log form submissions to console
    if (typeof document !== "undefined") {
      document.addEventListener("submit", (event) => {
        const form = event.target as HTMLFormElement;
        if (form.matches && form.matches(selector)) {
          const formData = {
            form_id: form.id || "no-id",
            form_name: form.name || "no-name",
            form_action: form.action || "no-action",
            form_method: form.method || "get",
            field_count: form.elements.length,
          };
          console.log(`[Mixpanel] ${eventName}:`, formData);
        }
      });
    }
    return;
  }

  // Use Mixpanel's track_forms for automatic tracking
  mixpanel.track_forms(selector, eventName, (form: HTMLFormElement) => {
    // Return additional properties to track with the event
    return {
      form_id: form.id || "no-id",
      form_name: form.name || "no-name",
      form_action: form.action || "no-action",
      form_method: form.method || "get",
      field_count: form.elements.length,
    };
  });

  console.log(`[Mixpanel] Form tracking enabled for selector: ${selector}`);
}

/**
 * Enable automatic link tracking
 *
 * This sets up automatic tracking for link clicks across the application.
 * Useful for tracking external links, downloads, or specific CTAs.
 *
 * @param selector - CSS selector for links to track
 * @param eventName - Event name to use for tracking
 *
 * @example
 * // Track external links
 * enableLinkTracking("a[href^='http']", "External Link Clicked");
 *
 * @example
 * // Track download links
 * enableLinkTracking("a[download]", "File Downloaded");
 */
export function enableLinkTracking(selector: string, eventName: string) {
  if (isDevelopment()) {
    console.log(
      `[Mixpanel] Link tracking enabled in development mode for selector: ${selector}`,
    );

    // In development, log link clicks to console
    if (typeof document !== "undefined") {
      document.addEventListener("click", (event) => {
        const target = event.target as HTMLElement;
        const link = target.closest(selector) as HTMLAnchorElement;
        if (link) {
          console.log(`[Mixpanel] ${eventName}:`, {
            link_url: link.href,
            link_text: link.textContent?.trim() || "no-text",
          });
        }
      });
    }
    return;
  }

  // Use Mixpanel's track_links for automatic tracking
  mixpanel.track_links(selector, eventName, (element: HTMLAnchorElement) => {
    return {
      link_url: element.href,
      link_text: element.textContent?.trim() || "no-text",
      link_target: element.target || "_self",
    };
  });

  console.log(`[Mixpanel] Link tracking enabled for selector: ${selector}`);
}
